/* =========================================================================
   ClueVine — icon set
   A small, cohesive stroke icon set. Stroke uses currentColor so icons inherit
   text color. Decorative by default (aria-hidden); pass a `title` to label one.
   ========================================================================= */

import type { ReactElement } from 'react';
import type { IconName } from '../engine/types';

interface IconProps {
  name: IconName;
  size?: number;
  title?: string;
  className?: string;
}

const PATHS: Record<IconName, ReactElement> = {
  cipher: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="2.5" />
      <path d="M8 8h0M12 8h0M16 8h4M8 12h4M16 12h0M8 16h0M12 16h4" />
    </>
  ),
  forge: (
    <>
      <path d="M12 3c2 2 2 4 0 6-2-2-2-4 0-6Z" />
      <path d="M19 9c-2.6.6-4 2-4.5 4.5C17 13 18.4 11.6 19 9Z" />
      <path d="M5 9c2.6.6 4 2 4.5 4.5C7 13 5.6 11.6 5 9Z" />
      <path d="M12 11v10" />
    </>
  ),
  tangle: (
    <>
      <path d="M5 8c5 0 9 8 14 8" />
      <path d="M5 16c5 0 9-8 14-8" />
    </>
  ),
  flame: (
    <path d="M12 3c1 3 4 4 4 8a4 4 0 1 1-8 0c0-1.5.6-2.4 1.4-3C10 9.7 11.4 8 12 3Z" />
  ),
  bulb: (
    <>
      <path d="M9 18h6" />
      <path d="M10 21h4" />
      <path d="M12 3a6 6 0 0 0-4 10.5c.8.8 1 1.4 1 2.5h6c0-1.1.2-1.7 1-2.5A6 6 0 0 0 12 3Z" />
    </>
  ),
  gear: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="m9.5 3-.6 2.2-1.6.9-2.2-.6L2.6 9.8l1.6 1.6v1.2l-1.6 1.6 2.5 4.3 2.2-.6 1.6.9.6 2.2h5l.6-2.2 1.6-.9 2.2.6 2.5-4.3-1.6-1.6v-1.2l1.6-1.6-2.5-4.3-2.2.6-1.6-.9-.6-2.2Z" />
    </>
  ),
  chart: (
    <>
      <path d="M4 20V10M10 20V4M16 20v-7M4 20h16" />
    </>
  ),
  history: (
    <>
      <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
      <path d="M3 4v4h4" />
      <path d="M12 8v4l3 2" />
    </>
  ),
  close: <path d="M6 6l12 12M18 6L6 18" />,
  back: <path d="M15 5l-7 7 7 7" />,
  'sound-on': (
    <>
      <path d="M4 9v6h4l5 4V5L8 9H4Z" />
      <path d="M16 9a3 3 0 0 1 0 6M18.5 7a6 6 0 0 1 0 10" />
    </>
  ),
  'sound-off': (
    <>
      <path d="M4 9v6h4l5 4V5L8 9H4Z" />
      <path d="M16 10l5 5M21 10l-5 5" />
    </>
  ),
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </>
  ),
  moon: <path d="M20 14.5A8 8 0 0 1 9.5 4 8 8 0 1 0 20 14.5Z" />,
  shuffle: (
    <>
      <path d="M3 7h4l10 10h4M3 17h4l3-3M14 8l3-3 3 3M17 19l3-3-3-3" />
    </>
  ),
  check: <path d="M5 13l4 4L19 7" />,
  leaf: (
    <>
      <path d="M5 19c0-8 6-14 14-14 0 8-6 14-14 14Z" />
      <path d="M5 19C9 15 13 11 17 9" />
    </>
  ),
  sparkle: (
    <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3Z" />
  ),
  trophy: (
    <>
      <path d="M7 4h10v4a5 5 0 0 1-10 0V4Z" />
      <path d="M7 6H4v1a3 3 0 0 0 3 3M17 6h3v1a3 3 0 0 1-3 3" />
      <path d="M10 14v3M14 14v3M8 20h8M9 20v-1h6v1" />
    </>
  ),
  share: (
    <>
      <circle cx="18" cy="5" r="2.5" />
      <circle cx="6" cy="12" r="2.5" />
      <circle cx="18" cy="19" r="2.5" />
      <path d="M8.2 10.8l7.6-4.4M8.2 13.2l7.6 4.4" />
    </>
  ),
  play: <path d="M7 4l12 8-12 8V4Z" />,
  plus: <path d="M12 5v14M5 12h14" />,
  home: (
    <>
      <path d="M4 11l8-7 8 7" />
      <path d="M6 10v9h12v-9" />
    </>
  ),
  ladder: <path d="M8 3v18M16 3v18M8 7.5h8M8 12h8M8 16.5h8" />,
  link: (
    <>
      <path d="M10.5 13.5a3.5 3.5 0 0 0 5 0l2-2a3.5 3.5 0 0 0-5-5l-1 1" />
      <path d="M13.5 10.5a3.5 3.5 0 0 0-5 0l-2 2a3.5 3.5 0 0 0 5 5l1-1" />
    </>
  ),
  grid: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M4 9.5h16M4 14.5h16M9.5 4v16M14.5 4v16" />
    </>
  ),
  flower: (
    <>
      <circle cx="12" cy="12" r="2.6" />
      <circle cx="12" cy="6.6" r="2.6" />
      <circle cx="17.4" cy="12" r="2.6" />
      <circle cx="12" cy="17.4" r="2.6" />
      <circle cx="6.6" cy="12" r="2.6" />
    </>
  ),
  quad: (
    <>
      <rect x="4" y="4" width="7" height="7" rx="1.6" />
      <rect x="13" y="4" width="7" height="7" rx="1.6" />
      <rect x="4" y="13" width="7" height="7" rx="1.6" />
      <rect x="13" y="13" width="7" height="7" rx="1.6" />
    </>
  ),
  help: (
    <>
      <circle cx="12" cy="12" r="9.2" />
      <path d="M9.2 9.2a3 3 0 0 1 5.6 1c0 2-2.9 2.6-2.9 4" />
      <path d="M12 17.4h.01" />
    </>
  ),
  thorn: (
    <>
      <path d="M6 17l4-6 4 4 4-9" />
      <circle cx="6" cy="17" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="10" cy="11" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="14" cy="15" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="18" cy="6" r="1.5" fill="currentColor" stroke="none" />
    </>
  ),
  lattice: (
    <>
      <rect x="3.5" y="3.5" width="17" height="17" rx="2.5" />
      <path d="M9.2 3.5v17M14.8 3.5v17M3.5 9.2h17M3.5 14.8h17" />
      <rect x="3.8" y="3.8" width="5.4" height="5.4" rx="1" fill="currentColor" stroke="none" opacity="0.4" />
      <rect x="14.8" y="14.8" width="5.4" height="5.4" rx="1" fill="currentColor" stroke="none" opacity="0.4" />
    </>
  ),
};

export function Icon({ name, size = 22, title, className }: IconProps) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      focusable="false"
    >
      {title ? <title>{title}</title> : null}
      {PATHS[name]}
    </svg>
  );
}
